"use client";

import type React from "react";

import { useState, useEffect } from "react";
import { Star, ChevronLeft, ChevronRight, Heart } from "lucide-react";
import { Card, CardContent } from "./Card";
import { Button } from "./Button";
import { selectPrice } from "../../utils/details";
import { QualyProms } from "../../utils/qualy-proms";
import { Link } from "react-router-dom";

interface Destination {
  id: number;
  title: string;
  distance: string;
  date: string;
  price: string;
  rating: number;
  images: any;
  ratings: any;
  prices: any;
  city: any;
  slug: any;
}

interface AccommodationCardProps {
  destination: Destination;
  setFavorito: any;
  favoritos: any;
  user: any;
}

export default function ExcursionCard({
  destination,
  setFavorito,
  favoritos,
  user,
}: AccommodationCardProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [autoSlideInterval, setAutoSlideInterval] =
    useState<NodeJS.Timeout | null>(null);

  // Auto-slide effect when hovered
  useEffect(() => {
    if (isHovered && destination.images.length > 1) {
      const interval = setInterval(() => {
        setCurrentImageIndex(
          (prevIndex) => (prevIndex + 1) % destination.images.length
        );
      }, 2000); // Change image every 2 seconds
      setAutoSlideInterval(interval);
    } else {
      if (autoSlideInterval) {
        clearInterval(autoSlideInterval);
        setAutoSlideInterval(null);
      }
    }

    return () => {
      if (autoSlideInterval) {
        clearInterval(autoSlideInterval);
      }
    };
  }, [isHovered, destination.images.length]);

  // Reset to first image when not hovered
  useEffect(() => {
    if (!isHovered) {
      setCurrentImageIndex(0);
    }
  }, [isHovered]);

  const nextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex(
      (prevIndex) => (prevIndex + 1) % destination.images.length
    );
  };

  const prevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex(
      (prevIndex) =>
        (prevIndex - 1 + destination.images.length) % destination.images.length
    );
  };

  const goToImage = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex(index);
  };

  const truncatedText = (text: string | null) => {
    if (!text) {
      return ""
    }

    if (text.length < 60) {
      return text;
    }

    return text.slice(0, 60) + '...';
  }
  return (
    <Card
      className="group hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative">
        {/* Main Image Container */}
        <div className="relative h-[130px] lg:h-[200px] overflow-hidden">
          <img
            src={
              destination.images[currentImageIndex]?.url ||
              "./src/assets/img/logo-en-blanco.png"
            }
            alt={`${destination.title} - Imagen ${currentImageIndex + 1}`}
            style={{
              width: "100%",
              objectFit: "cover",
              imageRendering: 'auto'
            }}
            className="
            min-h-[130px]
            lg:min-h-[200px]
            max-h-[130px]
            lg:max-h-[200px]
            object-cover group-hover:scale-105 transition-transform duration-300"
          />

          {/* Navigation Arrows - Only visible on hover */}
          {isHovered && destination.images.length > 1 && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 h-8 w-8 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10"
                onClick={prevImage}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 h-8 w-8 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10"
                onClick={nextImage}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </>
          )}

          {/* Image Dots Indicator - Only visible on hover */}
          {isHovered && destination.images.length > 1 && (
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              {destination.images.map((_: any, index: any) => (
                <button
                  key={index}
                  onClick={(e) => goToImage(index, e)}
                  className={`w-2 h-2 rounded-full transition-all duration-200 ${index === currentImageIndex
                    ? "bg-white"
                    : "bg-white/50 hover:bg-white/75"
                    }`}
                  aria-label={`Ir a imagen ${index + 1}`}
                />
              ))}
            </div>
          )}

          {/* Image Counter - Only visible on hover */}
          {isHovered && destination.images.length > 1 && (
            <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              {currentImageIndex + 1} / {destination.images.length}
            </div>
          )}
        </div>

        {/* Rating Badge */}
        <div className="absolute top-3 left-3  backdrop-blur-sm rounded-full px-2 py-1 flex items-center gap-1">
          <label
            className={`cursor-pointer ${user ? "" : "hidden"
              }`}
            style={{ top: "20px", right: "20px", zIndex: 2 }}
          >
            {favoritos?.data.filter((f: any) => f.excursion_id
              == destination.id)
              .length > 0 ? (
              <Heart className="w-6 h-6 transition-colors text-red fill-red" />
            ) : (
              <Heart className="w-6 h-6 transition-colors text-gray-400" />
            )}

            <button
              className="hidden"
              onClick={() => setFavorito(destination, favoritos)}
            ></button>
          </label>
        </div>


      </div>
      <Link
        className="font-bold"
        style={{ fontSize: "90%" }}
        to={`/excursion/${destination.slug}`}
      >
        <CardContent className="p-3 sm:p-4">
          <h3
            className="font-semibold text-base sm:text-lg group-hover:text-[#8B9456] transition-colors line-clamp-1 flex justify-between items-start"
            style={{ fontSize: "12px" }}
          >
            {truncatedText(destination.title)}
            <div className="bg-white/90 backdrop-blur-sm rounded-full px-2 py-1 flex items-center gap-1">
              <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
              <span className="text-sm font-medium">
                {QualyProms(destination?.ratings)}
              </span>
            </div>
          </h3>
          <p
            className="text-gray-600 text-xs sm:text-sm "
            style={{ fontSize: "12px" }}
          >
            {destination.city}
          </p>
          <p
            className="font-bold text-[#8B9456] text-base sm:text-lg"
            style={{ fontSize: "14px" }}
          >
            {`$ ${selectPrice(destination.price, [], 1).toLocaleString(
              "de-DE",
              {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }
            )}`}
          </p>
        </CardContent>
      </Link>
    </Card>
  );
}
